import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const WaiterPerformance = ({ waiterId, restaurantId: restaurantIdProp }) => {
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [performance, setPerformance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [dateRange, setDateRange] = useState('today');

    useEffect(() => {
        if (waiterId && restaurantId) {
            fetchPerformance();
        }
    }, [waiterId, restaurantId, dateRange]);

    const fetchPerformance = async () => {
        try {
            setLoading(true);
            const today = new Date();
            let startDate, endDate;

            switch (dateRange) {
                case 'today':
                    startDate = new Date(today);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(today);
                    endDate.setHours(23, 59, 59, 999);
                    break;
                case 'week':
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() - 7);
                    endDate = new Date(today);
                    break;
                case 'month':
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 1);
                    endDate = new Date(today);
                    break;
                default:
                    startDate = new Date(today);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(today);
                    endDate.setHours(23, 59, 59, 999);
            }

            // Fetch orders for the date range
            const ordersResponse = await axios.get(API_ENDPOINTS.ORDERS_WAITER(restaurantId), {
                params: {
                    waiterId,
                    today: dateRange === 'today' ? 'true' : 'false'
                }
            });

            const orders = ordersResponse.data.filter(order => {
                const orderDate = new Date(order.orderDate);
                return orderDate >= startDate && orderDate <= endDate;
            });

            // Calculate performance metrics
            const completedOrders = orders.filter(o => o.status === 'CLOSED');
            const activeOrders = orders.filter(o => !['CLOSED', 'CANCELLED'].includes(o.status));
            
            // Calculate average service time (time from order placed to served)
            const serviceTimes = completedOrders
                .filter(o => o.servedAt && o.orderDate)
                .map(o => {
                    const served = new Date(o.servedAt);
                    const placed = new Date(o.orderDate);
                    return (served - placed) / (1000 * 60); // Convert to minutes
                });

            const avgServiceTime = serviceTimes.length > 0
                ? (serviceTimes.reduce((a, b) => a + b, 0) / serviceTimes.length).toFixed(1)
                : 0;

            // Get unique tables served
            const tablesServed = new Set(completedOrders.map(o => o.tableId?._id || o.tableId || o.tableNumber));
            
            // Fetch feedback if available
            let avgRating = 0;
            try {
                const feedbackResponse = await axios.get(API_ENDPOINTS.FEEDBACK_GET, {
                    params: { restaurantId, staffId: waiterId }
                });
                const feedbacks = feedbackResponse.data.filter(f => {
                    const feedbackDate = new Date(f.createdAt);
                    return feedbackDate >= startDate && feedbackDate <= endDate;
                });
                if (feedbacks.length > 0) {
                    const ratings = feedbacks.map(f => f.staffRating).filter(r => r > 0);
                    if (ratings.length > 0) {
                        avgRating = (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch feedback:', error);
            }

            setPerformance({
                ordersHandled: orders.length,
                completedOrders: completedOrders.length,
                activeOrders: activeOrders.length,
                tablesServed: tablesServed.size,
                avgServiceTime: parseFloat(avgServiceTime),
                customerRating: parseFloat(avgRating),
                totalRevenue: completedOrders.reduce((sum, o) => sum + (o.finalAmount || 0), 0)
            });
        } catch (error) {
            console.error('Failed to fetch performance:', error);
            showToast('Failed to load performance data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-bold">Daily Performance</h2>
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="px-4 py-2 border rounded-lg"
                    >
                        <option value="today">Today</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 30 Days</option>
                    </select>
                </div>
                <p className="text-gray-600">Your performance metrics and statistics</p>
            </div>

            {!performance ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <span className="text-6xl block mb-4">📊</span>
                    <p className="text-gray-600 text-lg">No performance data available</p>
                </div>
            ) : (
                <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Orders Handled</p>
                                    <p className="text-3xl font-bold text-gray-800">{performance.ordersHandled}</p>
                                </div>
                                <span className="text-4xl">📋</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                {performance.completedOrders} completed, {performance.activeOrders} active
                            </p>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Tables Served</p>
                                    <p className="text-3xl font-bold text-gray-800">{performance.tablesServed}</p>
                                </div>
                                <span className="text-4xl">🪑</span>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Avg Service Time</p>
                                    <p className="text-3xl font-bold text-gray-800">
                                        {performance.avgServiceTime > 0 ? `${performance.avgServiceTime}m` : 'N/A'}
                                    </p>
                                </div>
                                <span className="text-4xl">⏱️</span>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Customer Rating</p>
                                    <p className="text-3xl font-bold text-gray-800">
                                        {performance.customerRating > 0 ? `${performance.customerRating}/5` : 'N/A'}
                                    </p>
                                </div>
                                <span className="text-4xl">⭐</span>
                            </div>
                            {performance.customerRating > 0 && (
                                <div className="mt-2">
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <span
                                                key={star}
                                                className={`text-lg ${
                                                    star <= performance.customerRating
                                                        ? 'text-yellow-400'
                                                        : 'text-gray-300'
                                                }`}
                                            >
                                                ★
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-indigo-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">Total Revenue</p>
                                    <p className="text-3xl font-bold text-gray-800">
                                        ₹{performance.totalRevenue.toFixed(2)}
                                    </p>
                                </div>
                                <span className="text-4xl">💰</span>
                            </div>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> Performance data is read-only. Ratings are based on customer feedback.
                        </p>
                    </div>
                </>
            )}

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default WaiterPerformance;

